import * as React from "react";

import { Viewport, ViewSmall, ViewMedium, ViewLarge } from "bernie-view";

import { UitkPrimaryButton } from "uitk-react-button";
import { UitkSpacing, UitkSpacingProps } from "uitk-react-spacing";
import { UitkFigureAspectRatioType as ExperimentalAspectRatioType } from "uitk-react-experimental-images";
import { UitkText } from "uitk-react-text";

import { FlexComponentProps, FlexComponent } from "typings/flexFramework/FlexComponent";
import { FlexModuleResult } from "typings/microserviceModels/content-flex-module";

import { ScrimCard } from "components/shared/ScrimCard/ScrimCard";
import { withMabTracker, MabTrackEvent } from "src/components/utility/analytics/MabTracker";
import { UitkScrimType as UitkExperimentalScrimType } from "uitk-react-experimental-scrim";
import { FAILED_MODEL_RENDER } from "src/config/systemEvents";
import { NOOP_LOGGER as logger } from "bernie-logger";
import { AnalyticsStore } from "bernie-core";

import { Action, FlexTrackingInfo, sendDelayedTrackEvent } from "components/utility/analytics/FlexAnalyticsUtils";

export interface EditorialBannerFlexComponent extends FlexComponent {
  model: FlexModuleResult;
}

export interface EditorialBannerProps extends FlexComponentProps {
  flexComponent: EditorialBannerFlexComponent;
  mabTrack?: (x: MabTrackEvent) => null;
  analytics: AnalyticsStore;
}

export const EditorialBanner = withMabTracker((props: EditorialBannerProps) => {
  try {
    const { mabTrack, flexComponent } = props;

    const { model, fmId } = flexComponent;
    const [{ media, text }] = model.items;

    /**
     * This is a temporary work around due to limitations of the call-to-action model that will be addressed in the future.
     * Parsing data from contributed HTML content should not be considered an accepted approach to be repeated elsewhere
     */
    const htmlText = text as string;

    const [, title] = /<h3>(.*?)<\/h3>/g.exec(htmlText);
    const [, subtitle] = /<p class="subtitle">(.*?)<\/p>/g.exec(htmlText);
    const [, ctaLabel] = /<p><button>(.*?)<\/button><\/p>/g.exec(htmlText);

    // Media content
    const [desktopMedia, mobileMedia] = media;
    const { mediaUrl: desktopMediaURL } = desktopMedia;
    const { mediaUrl: mobileMediaURL } = mobileMedia;
    const { clickUrl } = desktopMedia;

    // Component display properties
    const className = "EditorialBanner";
    const spacing: UitkSpacingProps = { margin: { inline: "three", blockstart: "three" } };

    const trackingInfo: FlexTrackingInfo = {
      moduleName: "EditorialBanner",
      action: Action.CLICK,
      linkName: "CTA button clicked",
      rfrr: "Banner_Click",
    };

    const cardClicked = mabTrack
      ? () => {
          const mabTrackData: MabTrackEvent = {
            moduleName: "List_your_property",
            eventTarget: "List_your_property",
            eventType: "homepageClick.nonPath",
            rfrr: "LYP_Homepage",
          };
          mabTrack(mabTrackData);
          sendDelayedTrackEvent(trackingInfo, props.analytics);
        }
      : () => {
          sendDelayedTrackEvent(trackingInfo, props.analytics);
        };

    const scrimCardProps = {
      id: `editorial-banner-${fmId}`,
      className,
      titleSlot: <h3 className="uitk-type-700">{title}</h3>,
      subtitleSlot: <UitkText size={500}>{subtitle}</UitkText>,
      footerSlot: (
        <UitkSpacing margin={{ blockend: "one" }}>
          <div>
            <UitkSpacing margin={{ block: "three" }}>
              <UitkPrimaryButton tag="a" onClick={cardClicked} href={clickUrl}>
                {ctaLabel}
              </UitkPrimaryButton>
            </UitkSpacing>
          </div>
        </UitkSpacing>
      ),
      spacing,
    };

    return (
      <Viewport>
        <ViewSmall>
          <ScrimCard
            {...scrimCardProps}
            aspectRatio={ExperimentalAspectRatioType.R1_1}
            backgroundImageURL={mobileMediaURL}
            scrimType={UitkExperimentalScrimType.OVERLAY_TOP}
            marginClass="s-t-padding-zero"
          />
        </ViewSmall>
        <ViewMedium>
          <ScrimCard
            {...scrimCardProps}
            aspectRatio={ExperimentalAspectRatioType.R21_9}
            backgroundImageURL={desktopMediaURL}
            scrimType={UitkExperimentalScrimType.OVERLAY_LEFT}
            marginClass="s-t-padding-zero"
          />
        </ViewMedium>
        <ViewLarge>
          <ScrimCard
            {...scrimCardProps}
            titleSlot={<h3 className="uitk-type-800">{title}</h3>}
            aspectRatio={ExperimentalAspectRatioType.R4_1}
            backgroundImageURL={desktopMediaURL}
            scrimType={UitkExperimentalScrimType.OVERLAY_LEFT}
            marginClass="s-t-padding-zero"
          />
        </ViewLarge>
      </Viewport>
    );
  } catch (error) {
    logger.logEvent(FAILED_MODEL_RENDER, "Unexpected error occurred while rendering EditorialBanner module.", error);

    return null;
  }
});

export default EditorialBanner;
